import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Info, AlertTriangle, CheckCircle, Megaphone, Eye, MessageCircle, Settings, UserCheck, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SystemNotification, useMarkNotificationAsRead } from "@/hooks/use-notifications";
import { useNavigate } from "react-router-dom";

interface NotificationItemProps {
  notification: SystemNotification;
}

export function NotificationItem({ notification }: NotificationItemProps) {
  const markAsRead = useMarkNotificationAsRead();
  const navigate = useNavigate();

  const getIcon = () => {
    // Se for notificação de ticket, usar ícone específico
    if (notification.metadata?.interaction_type) {
      switch (notification.metadata.interaction_type) {
        case 'message':
          return <MessageCircle className="h-3 w-3 text-blue-500" />;
        case 'status_change':
          return <Settings className="h-3 w-3 text-orange-500" />;
        case 'assignment':
          return <UserCheck className="h-3 w-3 text-green-500" />;
        case 'priority_change':
          return <AlertCircle className="h-3 w-3 text-red-500" />;
        default:
          return <MessageCircle className="h-3 w-3 text-blue-500" />;
      }
    }

    // Ícones padrão para outros tipos
    switch (notification.type) {
      case "warning":
        return <AlertTriangle className="h-3 w-3 text-warning" />;
      case "success":
        return <CheckCircle className="h-3 w-3 text-success" />;
      case "announcement":
        return <Megaphone className="h-3 w-3 text-primary" />;
      default:
        return <Info className="h-3 w-3 text-primary" />;
    }
  };

  const getTypeLabel = () => {
    // Se for notificação de ticket, usar label específico
    if (notification.metadata?.interaction_type) {
      switch (notification.metadata.interaction_type) {
        case 'message':
          return "Mensagem";
        case 'status_change':
          return "Status";
        case 'assignment':
          return "Atribuição";
        case 'priority_change':
          return "Prioridade";
        default:
          return "Ticket";
      }
    }

    // Labels padrão para outros tipos
    switch (notification.type) {
      case "warning":
        return "Aviso";
      case "success":
        return "Sucesso";
      case "announcement":
        return "Anúncio";
      default:
        return "Info";
    }
  };

  const handleMarkAsRead = () => {
    if (!notification.is_read) {
      markAsRead.mutate(notification.id);
    }
  };

  const handleNotificationClick = () => {
    // Marcar como lida ao clicar
    if (!notification.is_read) {
      markAsRead.mutate(notification.id);
    }

    // Navegar para o link se existir
    if (notification.metadata?.redirect_url) {
      navigate(notification.metadata.redirect_url);
    }
  };

  return (
    <div 
      className={`p-3 hover:bg-muted/50 transition-colors ${!notification.is_read ? 'bg-primary/5' : ''} ${notification.metadata?.redirect_url ? 'cursor-pointer' : ''}`}
      onClick={notification.metadata?.redirect_url ? handleNotificationClick : undefined}
    >
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0 mt-0.5">
          {getIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-medium leading-tight truncate">
                  {notification.title}
                </h4>
                {!notification.is_read && (
                  <div className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                )}
              </div>
              <Badge variant="outline" className="text-xs mt-1 w-fit">
                {getTypeLabel()}
              </Badge>
            </div>
            {!notification.is_read && !notification.metadata?.redirect_url && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAsRead}
                className="h-6 w-6 p-0 text-xs flex-shrink-0"
                disabled={markAsRead.isPending}
              >
                <Eye className="h-3 w-3" />
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed mb-2 line-clamp-2">
            {notification.content}
          </p>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(notification.created_at), {
                addSuffix: true,
                locale: ptBR,
              })}
            </p>
            {notification.metadata?.redirect_url && (
              <span className="text-xs text-primary font-medium">
                Clique para ver →
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}